import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, Truck, Phone, Pencil, Trash2, AlertTriangle, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

export default function Couriers() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", active: true });
  const [history, setHistory] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reassign, setReassign] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [reassigning, setReassigning] = useState(false);

  const load = () => { setLoading(true); api.get("/couriers").then((r) => setCouriers(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm({ name: "", email: "", password: "", phone: "", active: true }); setShowForm(true); };
  const openEdit = (c) => { setEdit(c); setForm({ name: c.name, email: c.email, password: "", phone: c.phone || "", active: c.active }); setShowForm(true); };

  const save = async () => {
    try {
      if (edit) await api.put(`/couriers/${edit.id}`, form);
      else await api.post("/couriers", form);
      toast.success("Livreur enregistré"); setShowForm(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/couriers/${delTarget.id}`);
      toast.success("Livreur supprimé"); setDelTarget(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const openReassign = (c) => { setReassign(c); setReassignTo(""); };
  const confirmReassign = async () => {
    if (!reassign || !reassignTo) return;
    setReassigning(true);
    try {
      const { data } = await api.post(`/couriers/${reassign.id}/reassign`, { target_courier_id: reassignTo });
      toast.success(data.reassigned > 0 ? `${data.reassigned} commande(s) réattribuée(s) à ${data.target}` : "Aucune commande active à réattribuer");
      setReassign(null); setReassignTo(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setReassigning(false); }
  };

  const openHistory = async (c) => {
    const { data } = await api.get(`/orders?courier_id=${c.id}&page_size=50`);
    setHistory({ courier: c, orders: data.items });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Livreurs</h1><p className="text-slate-500 text-sm mt-1">Gestion de l'équipe de livraison</p></div>
        <Button data-testid="courier-new-btn" onClick={openNew} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-2" /> Nouveau livreur</Button>
      </div>

      {loading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {couriers.map((c) => (
            <div key={c.id} data-testid={`courier-mgmt-${c.id}`} className="bg-white border rounded-2xl p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2"><div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center"><Truck className="w-5 h-5" /></div><div><div className="font-semibold">{c.name}</div><div className="text-xs text-slate-400">{c.email}</div></div></div>
                <span className={`text-xs rounded-full px-2 py-0.5 ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Actif" : "Inactif"}</span>
              </div>
              <div className="text-sm text-slate-500 mt-2 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {c.phone || "—"}</div>
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openHistory(c)} data-testid={`courier-history-${c.id}`}>Historique</Button>
                <Button variant="outline" size="sm" onClick={() => openReassign(c)} data-testid={`courier-reassign-${c.id}`} title="Réattribuer les commandes"><ArrowLeftRight className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`courier-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                {isSuperAdmin && (
                  <Button variant="outline" size="sm" onClick={() => setDelTarget(c)} data-testid={`courier-delete-${c.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Modifier" : "Nouveau"} livreur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom *</Label><Input data-testid="crf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email *</Label><Input data-testid="crf-email" type="email" value={form.email} disabled={!!edit} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>{edit ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</Label><Input data-testid="crf-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Livreur123!" className="mt-1" /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Actif</label>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button data-testid="crf-submit" onClick={save} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent>
          {history && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Commandes de {history.courier.name}</DialogTitle></DialogHeader>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.orders.map((o) => (
                  <div key={o.id} className="flex justify-between border rounded-xl px-3 py-2 text-sm">
                    <span className="font-mono font-semibold text-brand-700">{o.order_number}</span>
                    <span>{o.client_name}</span>
                    <span className="text-slate-400">{o.status}</span>
                  </div>
                ))}
                {history.orders.length === 0 && <p className="text-slate-400 text-center py-6">Aucune commande attribuée</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!reassign} onOpenChange={(v) => !v && setReassign(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-brand-600" /> Réattribuer les commandes</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Transférer toutes les commandes actives (non livrées) de <b>{reassign?.name}</b> vers un autre livreur.</p>
          <div className="mt-1">
            <Label>Nouveau livreur</Label>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger data-testid="reassign-select" className="mt-1"><SelectValue placeholder="Choisir un livreur" /></SelectTrigger>
              <SelectContent>
                {couriers.filter((c) => c.id !== reassign?.id && c.active).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setReassign(null)}>Annuler</Button>
            <Button data-testid="reassign-confirm" onClick={confirmReassign} disabled={reassigning || !reassignTo} className="bg-brand-600 hover:bg-brand-700">
              {reassigning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réattribuer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" /> Supprimer le livreur</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Voulez-vous vraiment supprimer <b>{delTarget?.name}</b> ? Son compte et son accès seront définitivement supprimés. Cette action est irréversible.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDelTarget(null)}>Annuler</Button>
            <Button data-testid="courier-delete-confirm" onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Supprimer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
